// Pixel helpers for pictures (Touch Up + Clean Logo).
const dist2 = (d, i, r, g, b) => { const dr = d[i] - r, dg = d[i + 1] - g, db = d[i + 2] - b; return dr * dr + dg * dg + db * db; };

// Clear every pixel joined to the seeds that is close to colour [r,g,b]; edge pixels that are only partly
// that colour fade out instead of leaving a hard halo.
export function floodClear(ctx, w, h, seeds, rgb, tol) {
  const im = ctx.getImageData(0, 0, w, h), d = im.data, [r, g, b] = rgb, lim = tol * tol * 3, soft = 4 * lim, seen = new Uint8Array(w * h), st = [...seeds];
  while (st.length) {
    const p = st.pop(); if (seen[p]) continue; seen[p] = 1;
    const i = p * 4; if (d[i + 3] === 0) continue;
    const dd = dist2(d, i, r, g, b);
    if (dd > lim) { if (dd < soft) d[i + 3] = Math.round(d[i + 3] * Math.min(1, (Math.sqrt(dd) - Math.sqrt(lim)) / Math.sqrt(lim))); continue; }
    d[i + 3] = 0; const x = p % w, y = (p / w) | 0;
    if (x > 0) st.push(p - 1); if (x < w - 1) st.push(p + 1); if (y > 0) st.push(p - w); if (y < h - 1) st.push(p + w);
  }
  ctx.putImageData(im, 0, 0);
}
export const clickClear = (ctx, w, h, x, y, tol) => { const d = ctx.getImageData(x, y, 1, 1).data; floodClear(ctx, w, h, [y * w + x], [d[0], d[1], d[2]], tol); };

// Soften the see-through edge by 1px (blur the transparency only where solid meets clear).
export function smoothEdges(ctx, w, h) {
  const im = ctx.getImageData(0, 0, w, h), d = im.data, a = new Uint8ClampedArray(w * h);
  for (let p = 0; p < w * h; p++) a[p] = d[p * 4 + 3];
  for (let y = 1; y < h - 1; y++) for (let x = 1; x < w - 1; x++) {
    const p = y * w + x; let mn = 255, mx = 0, sum = 0;
    for (let j = -1; j <= 1; j++) for (let k = -1; k <= 1; k++) { const v = a[p + j * w + k]; sum += v; if (v < mn) mn = v; if (v > mx) mx = v; }
    if (mx - mn > 40) d[p * 4 + 3] = Math.min(a[p], Math.round(sum / 9 + a[p]) / 2);
  }
  ctx.putImageData(im, 0, 0);
}

// Bounding box of the visible (non see-through) pixels.
export function opaqueBox(ctx, w, h) {
  const d = ctx.getImageData(0, 0, w, h).data; let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) if (d[(y * w + x) * 4 + 3] > 8) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
  return x1 < 0 ? null : { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}

// Plain background = the four corners are (nearly) the same solid colour.
export function plainBackground(ctx, w, h) {
  const px = (x, y) => ctx.getImageData(x, y, 1, 1).data;
  const cs = [px(0, 0), px(w - 1, 0), px(0, h - 1), px(w - 1, h - 1)];
  if (cs.some((c) => c[3] < 200)) return null;
  const [r, g, b] = cs[0];
  return cs.every((c) => dist2(c, 0, r, g, b) < 30 * 30 * 3) ? [r, g, b] : null;
}

export const loadImg = (src) => new Promise((ok, bad) => { const i = new Image(); i.crossOrigin = "anonymous"; i.onload = () => ok(i); i.onerror = () => bad(new Error("Could not open the picture")); i.src = src; });
const canvasOf = (img, w = img.naturalWidth || img.width, h = img.naturalHeight || img.height) => { const c = document.createElement("canvas"); c.width = w; c.height = h; c.getContext("2d", { willReadFrequently: true }).drawImage(img, 0, 0, w, h); return c; };
const cropOf = (src, b) => { const c = document.createElement("canvas"); c.width = b.w; c.height = b.h; c.getContext("2d").drawImage(src, b.x, b.y, b.w, b.h, 0, 0, b.w, b.h); return c; };

// Clean Logo: clear a plain background (from the edges inwards), soften the cut edge, trim empty margins.
// Returns null when there is no plain background (caller can fall back to AI removal).
export async function cleanLogo(src, origSrc, { aiCut } = {}) {
  const img = await loadImg(src), c = canvasOf(img), w = c.width, h = c.height, ctx = c.getContext("2d", { willReadFrequently: true });
  if (!aiCut) {
    const bg = plainBackground(ctx, w, h); if (!bg) return null;
    const seeds = []; for (let x = 0; x < w; x++) seeds.push(x, (h - 1) * w + x); for (let y = 0; y < h; y++) seeds.push(y * w, y * w + w - 1);
    floodClear(ctx, w, h, seeds, bg, 28);
  }
  smoothEdges(ctx, w, h);
  const box = opaqueBox(ctx, w, h); if (!box) return null;
  const pad = 2, b = { x: Math.max(0, box.x - pad), y: Math.max(0, box.y - pad) };
  b.w = Math.min(w - b.x, box.w + pad * 2); b.h = Math.min(h - b.y, box.h + pad * 2);
  const orig = await loadImg(origSrc || src);
  return { png: cropOf(c, b).toDataURL("image/png"), orig: cropOf(canvasOf(orig, w, h), b).toDataURL("image/png"), box: b };
}
